import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { formatError } from '../utils/errorHandler';
import './Login.css';

const ResetPassword = () => {
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);
  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setError(t('invalidResetLink'));
      setTokenValid(false);
    }
  }, [token, t]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`/api/auth/verify-reset-token/${token}`);
      if (response.data.success) {
        setTokenValid(true);
      } else {
        setTokenValid(false);
        setError(t('invalidToken'));
      }
    } catch (verifyError) {
      setTokenValid(false);
      setError(formatError(verifyError) || t('invalidToken'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (password !== confirmPassword) {
      setError(t('passwordsNoMatch'));
      return;
    }

    if (password.length < 6) {
      setError(t('passwordMinLength'));
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('/api/auth/reset-password', {
        token,
        password
      });

      if (response.data.success) {
        setSuccess(t('resetSuccess'));
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (resetError) {
      setError(formatError(resetError) || t('resetFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (tokenValid === null) {
    return (
      <div className="login-container">
        <div className="login-card">
          <h2>{t('verifyToken')}</h2>
        </div>
      </div>
    );
  }

  if (tokenValid === false) {
    return (
      <div className="login-container">
        <div className="login-card">
          <h2>{t('resetPasswordTitle')}</h2>
          {error && <div className="error">{error}</div>}
          <p>
            <a href="/login">{t('backToLogin')}</a>
          </p>
          <p>
            <a href="/login">{t('requestNewLink')}</a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <h2>{t('resetPasswordTitle')}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>{t('newPassword')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
            />
          </div>
          <div className="form-group">
            <label>{t('confirmPassword')}</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
            />
          </div>
          {error && <div className="error">{error}</div>}
          {success && <div className="success">{success}</div>}
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? t('resetting') : t('resetPassword')}
          </button>
        </form>
        <p className="login-link">
          <a href="/login">{t('backToLogin')}</a>
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
